import { useMemo, useRef, useEffect, useState } from 'react';
import type { HtmlContent } from '@mawkaa/types';
import { Code, AlertTriangle } from 'lucide-react';

interface HtmlBlockProps {
  content: HtmlContent;
  isEditing?: boolean;
}

/**
 * HtmlBlock renders user-provided HTML code in a sandboxed iframe
 * to prevent XSS attacks and isolate custom scripts from the main application
 */
export function HtmlBlock({ content, isEditing }: HtmlBlockProps) {
  const { code = '', sandbox = true } = content;
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(200);

  // Generate iframe content with proper document structure
  const iframeContent = useMemo(() => {
    if (!code) return '';
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>
            * { box-sizing: border-box; }
            body { margin: 0; padding: 16px; font-family: system-ui, sans-serif; }
          </style>
        </head>
        <body>${code}</body>
      </html>
    `;
  }, [code]);

  // Auto-resize iframe based on content height
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc?.body) {
          const height = doc.body.scrollHeight;
          setIframeHeight(Math.max(100, Math.min(height + 32, 800)));
        }
      } catch {
        // Cross-origin restriction - use default height
      }
    };

    iframe.addEventListener('load', handleLoad);
    return () => iframe.removeEventListener('load', handleLoad);
  }, [iframeContent]);

  if (!code && isEditing) {
    return (
      <section className="px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-100 rounded-lg p-8 text-center">
            <Code className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-muted-foreground">Bloc HTML personnalisé</p>
            <p className="text-sm text-muted-foreground mt-2">
              Ajoutez votre code HTML, CSS ou JavaScript
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (!code) {
    return null;
  }

  // Sandbox attributes for security isolation
  const sandboxAttrs = sandbox
    ? 'allow-scripts allow-same-origin'
    : 'allow-scripts allow-same-origin allow-forms allow-popups';

  // In edit mode, show preview warning
  if (isEditing) {
    return (
      <section className="px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="border-2 border-dashed border-yellow-400 rounded-lg p-2">
            <div className="flex items-center justify-between mb-2">
              <div className="bg-yellow-50 text-yellow-800 text-xs px-3 py-1 rounded inline-flex items-center gap-1">
                <Code className="w-3 h-3" />
                Bloc HTML (aperçu sandboxé)
              </div>
              <div className="text-xs text-yellow-600 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Code isolé pour votre sécurité
              </div>
            </div>
            <iframe
              ref={iframeRef}
              srcDoc={iframeContent}
              sandbox={sandboxAttrs}
              className="w-full border-0 rounded"
              style={{ height: iframeHeight }}
              title="HTML Block Preview"
            />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <iframe
          ref={iframeRef}
          srcDoc={iframeContent}
          sandbox={sandboxAttrs}
          className="w-full border-0"
          style={{ height: iframeHeight }}
          title="HTML Block Content"
        />
      </div>
    </section>
  );
}
